/**
 * Member token issued at login (API AuthResponseDto.memberToken). It binds Credit Score requests
 * to the logged-in member, because the portal's access token is shared by every member. Kept next
 * to the access token in localStorage and removed with it on logout.
 */
const MEMBER_TOKEN_KEY = 'member-token';

interface IStoredMemberToken {
  token: string;
  expiresAt?: string;
}

export function saveMemberToken(token?: string | null, expiresAtUTC?: string | null): void {
  try {
    if (!token) {
      localStorage.removeItem(MEMBER_TOKEN_KEY);
      return;
    }
    const value: IStoredMemberToken = { token, expiresAt: expiresAtUTC ?? undefined };
    localStorage.setItem(MEMBER_TOKEN_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable: Credit Score falls back to the current portal.
  }
}

export function getMemberToken(now: Date = new Date()): string | null {
  try {
    const raw = localStorage.getItem(MEMBER_TOKEN_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as IStoredMemberToken;
    if (stored.expiresAt && new Date(stored.expiresAt).getTime() <= now.getTime()) return null;
    return stored.token || null;
  } catch {
    return null;
  }
}

/**
 * The stored token even when past its expiry: Credit Score calls still send it, so the API answers
 * 401 (code 161) and the member is signed out with a reason instead of Credit Score vanishing.
 */
export function getStoredMemberToken(): string | null {
  try {
    const raw = localStorage.getItem(MEMBER_TOKEN_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as IStoredMemberToken).token || null;
  } catch {
    return null;
  }
}

export function clearMemberToken(): void {
  try {
    localStorage.removeItem(MEMBER_TOKEN_KEY);
  } catch {
    // nothing to clear
  }
}
